import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { TileLayer } from "react-leaflet";

import MapConfig from "services/map/MapConfig";
import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import DisplaysActioner from "services/displays/actioner";

import LeafletMap from "components/LeafletMap";
import DisplaysLayers from "components/DisplaysLayers";

function MapComponent({
  displays,
  center,
  initFloorsData,
  maxZoom,
  minZoom,
  map,
  setSelectedLayers,
  setConfigParam,
  tilesUrl,
  zoom,
}) {
  useEffect(() => {
    async function initializeData() {
      await initFloorsData();
    }

    initializeData();
  }, [initFloorsData]);

  const formattedDisplays = useMemo(() => {
    return {
      ...(displays || {}),
      ...(map || {}),
      displays: displays?.items,
    };
  }, [map, displays]);

  function getMapRadius(currentMap) {
    return MapUtils.getRadiusInMeters(
      currentMap.getBounds().getNorthEast(),
      currentMap.getCenter()
    );
  }

  function handleZoomEnd(e) {
    const currentMap = e?.target;

    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentZoom", currentMap.getZoom());
  }

  function handleMoveEnd(e) {
    const currentMap = e?.target;

    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentPosition", currentMap.getCenter());
  }

  function handleOnLoad(e) {
    const currentMap = e?.target;
    setConfigParam("currentRadius", getMapRadius(currentMap._map));
    setConfigParam("currentPosition", currentMap._map.getCenter());
  }

  function handleGetLabelColor(layer) {
    if (layer?.type === "display") {
      const apiColor = layer?.meta_data?.color || "default";
      return MapConfig?.iot?.display?.color?.[apiColor];
    }

    return null;
  }

  function getDisplayLabelWithSymbols(computedValue = "", symbolConfig = {}) {
    const tokenKeys = Object.keys(symbolConfig);

    if (!tokenKeys.length || typeof computedValue !== "string") {
      return computedValue;
    }

    const escapedTokens = tokenKeys.map((token) =>
      token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    const tokenRegex = new RegExp(`(${escapedTokens.join("|")})`, "g");

    const parsedLabel = computedValue
      .split(tokenRegex)
      .filter((part) => part !== "")
      .map((part, index) => {
        const symbolName = symbolConfig?.[part];
        const CurrentSymbol = MapConfig?.iot?.display?.symbol?.[symbolName];

        if (!CurrentSymbol) {
          return part;
        }

        return <CurrentSymbol key={`${part}-${index}`} />;
      });

    if (parsedLabel.every((part) => typeof part === "string")) {
      return parsedLabel.join("");
    }

    return parsedLabel;
  }

  function handleGetLabelLength(layer) {
    const computedValue = layer?.meta_data?.computed_value || "";
    let labelWidth = computedValue.length;
    let nbSymbols = 0;

    if (layer?.meta_data?.has_symbol) {
      const symbolConfig = layer?.meta_data?.symbol_config || {};
      labelWidth = Object.keys(symbolConfig).reduce((previous, currentKey) => {
        if (previous.includes(currentKey)) {
          nbSymbols += 1;
        }

        return previous.replace(currentKey, "");
      }, computedValue).length;

      labelWidth += nbSymbols;
    }

    if (layer?.meta_data?.has_pictogram && layer.apiType === "STRAMATEL") {
      labelWidth -= 1;
    }

    return {
      length: labelWidth,
      nbSymbols,
    };
  }

  function handleGetScrolling(layer) {
    if (!layer?.meta_data?.scrolling || !layer?.meta_data?.max_digits) {
      return false;
    }

    const labelWidth = handleGetLabelLength(layer).length;

    return labelWidth > layer?.meta_data?.max_digits;
  }

  function handleGetAlternating(layer) {
    if (
      !layer?.meta_data?.message_delimiters ||
      !layer?.meta_data?.computed_value
    ) {
      return null;
    }

    const delimiters = layer?.meta_data?.message_delimiters;
    const computedValue = layer?.meta_data?.computed_value;

    const foundDelimiter = delimiters.find((delimiter) =>
      computedValue.includes(delimiter)
    );

    if (!foundDelimiter) {
      return null;
    }

    const messages = computedValue
      .split(foundDelimiter)
      .filter((msg) => msg.trim() !== "");

    if (messages.length <= 1) {
      return null;
    }

    if (!layer?.meta_data?.has_symbol) {
      return messages;
    }

    const symbolConfig = layer?.meta_data?.symbol_config || {};
    return messages.map((message) =>
      getDisplayLabelWithSymbols(message, symbolConfig)
    );
  }

  function handleGetLayerLabel(layer) {
    if (layer.type === "display") {
      if (layer.status === "STAND_BY") {
        return "";
      }
      if (layer?.meta_data?.has_symbol) {
        return getDisplayLabelWithSymbols(
          layer?.meta_data?.computed_value || "",
          layer?.meta_data?.symbol_config || {}
        );
      }

      let value = layer?.meta_data?.computed_value;
      // Some stramatel displays have a pictogram as first character, we remove it
      if (layer?.meta_data?.has_pictogram && layer.apiType === "STRAMATEL") {
        value = value.substring(1, value.length);
      }
      return value;
    }

    return null;
  }

  return (
    <LeafletMap
      preferCanvas
      style={{
        width: "100%",
        height: "100%",
      }}
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      onZoomEnd={handleZoomEnd}
      onMoveEnd={handleMoveEnd}
    >
      <TileLayer url={MapConfig.defaultTileUrl} />
      <TileLayer
        url={tilesUrl}
        onLoad={handleOnLoad}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />

      <DisplaysLayers
        map={formattedDisplays}
        getLabelColor={handleGetLabelColor}
        getLabel={handleGetLayerLabel}
        getLabelLength={handleGetLabelLength}
        getScrolling={handleGetScrolling}
        getAlternating={handleGetAlternating}
        onSelectLayers={setSelectedLayers}
      />
    </LeafletMap>
  );
}

const mapStateToProps = (state) => ({
  map: state.Displays.map,
  center: MapUtils.getCenter(state.Map),
  maxZoom: MapUtils.getMaxZoom(state.Map),
  minZoom: MapUtils.getMinZoom(state.Map),
  displays: state.Displays.list,
  tilesUrl: MapUtils.getTilesUrl(state.Map),
  zoom: MapUtils.getZoom(state.Map),
});

const mapDispatchToProps = (dispatch) => ({
  retrieveLayersInfos: () => dispatch(DisplaysActioner.retrieveLayersInfos()),
  setConfigParam: (key, value) =>
    dispatch(MapActioner.setConfigParam(key, value)),
  setSelectedLayers: (layers) =>
    dispatch(DisplaysActioner.setSelectedLayers(layers)),
  initFloorsData: () =>
    dispatch(MapActioner.initFloorsDataFromModule("park_stats")),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
